import { useState, ChangeEvent } from 'react';
import { Button, Input } from 'components/atoms/index';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { academyAuthApi } from '@/services/api';
import { Modal } from '@/components/organisms/Modal/Modal';
import { getSlugPrefix } from '@/utils/slug';

export default function AcademyFindId() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const slugPrefix = getSlugPrefix(pathname);
  const buildPath = (path: string) => (slugPrefix ? `${slugPrefix}${path}` : path);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ academyName: '', businessNumber: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [foundId, setFoundId] = useState('');

  const maskLoginId = (loginId: string) => {
    if (!loginId) return '';
    if (loginId.length <= 3) return '***';
    return loginId.slice(0, -3) + '***';
  };

  const handleChange = (field: 'academyName' | 'businessNumber') => (e: ChangeEvent<HTMLInputElement>) => {
    const value = field === 'businessNumber' ? e.target.value.replace(/\\D/g, '') : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFindId = async () => {
    if (!form.academyName.trim() || !form.businessNumber.trim()) {
      await Modal.alert({ message: '학원명과 사업자 등록 번호를 모두 입력해주세요.' });
      return;
    }
    setIsLoading(true);
    try {
      const response = await academyAuthApi.findId({
        academyName: form.academyName.trim(),
        businessNumber: form.businessNumber.trim(),
      });
      if (response?.success && response?.data?.loginId) {
        setFoundId(maskLoginId(response.data.loginId));
        setStep(2);
      } else {
        await Modal.alert({ message: response?.message || '아이디 정보를 찾을 수 없습니다.' });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || '아이디 찾기 중 오류가 발생했습니다.';
      await Modal.alert({ message });
    } finally {
      setIsLoading(false);
    }
  };

  const goLogin = () => navigate(buildPath('/academy/login'));

  return (
    <main className='flex flex-1 justify-center my-10 w-full bg-white md:py-10 md:bg-transparent'>
      <div className='flex-1 pb-8 lg:px-10'>
        <h1 className='pb-5 heading1'>아이디 찾기</h1>
        {step === 1 && (
          <section>
            <div className='flex flex-col gap-5'>
              <Input
                label='학원명'
                placeholder='학원명을 입력해주세요.'
                maxLength={100}
                value={form.academyName}
                onChange={handleChange('academyName')}
                disabled={isLoading}
              />

              <Input
                label='사업자 등록 번호'
                placeholder='사업자 등록 번호를 입력해주세요.'
                maxLength={30}
                inputMode='numeric'
                pattern='[0-9]*'
                value={form.businessNumber}
                onChange={handleChange('businessNumber')}
                disabled={isLoading}
              />
            </div>

            <Button className='mt-10 w-full' onClick={handleFindId} disabled={isLoading}>
              {isLoading ? '조회 중...' : '아이디 찾기'}
            </Button>
          </section>
        )}

        {step === 2 && (
          <section>
            <div className='p-5 text-center bg-gray-50 rounded-lg'>
              <p className='text-lg font-bold'>
                회원님의 아이디는 {foundId || '확인되지 않았습니다.'}
              </p>
            </div>
            <div className='flex gap-5 mt-10'>
              <Button className='w-full' intent='secondary'>
                <Link to={buildPath('/academy/find_password')}>비밀번호 찾기</Link>
              </Button>
              <Button className='w-full' onClick={goLogin}>
                로그인 하기
              </Button>
            </div>
          </section>
        )}
      </div>
    </main>
  );
}